/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import {
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonUnderline,
  MenuButtonStrikethrough,
  MenuButtonCode,
  MenuButtonCodeBlock,
  MenuButtonBlockquote,
  MenuButtonBulletedList,
  MenuButtonOrderedList,
  MenuButtonHorizontalRule,
  MenuButtonUndo,
  MenuButtonRedo,
  MenuButtonRemoveFormatting,
  MenuButtonAlignLeft,
  MenuButtonAlignCenter,
  MenuButtonAlignRight,
  MenuButtonAlignJustify,
  MenuButtonEditLink,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  RichTextEditorProvider,
  RichTextField,
  LinkBubbleMenu,
  LinkBubbleMenuHandler,
} from 'mui-tiptap';
import InputGroupV2 from '../InputGroupV2';

const EDITOR_LINE_HEIGHT = 24;
const EDITOR_VERTICAL_PADDING = 16;

const getContentMinHeight = (rows) => rows * EDITOR_LINE_HEIGHT + EDITOR_VERTICAL_PADDING;

const getRichTextFieldSx = (disabled) => ({
  bgcolor: disabled ? 'transparent' : 'background.paper',
  ...(disabled && { cursor: 'default' }),
});

const getRichTextContentSx = (disabled, rows, isProcedimento) => (theme) => ({
  minHeight: getContentMinHeight(rows),
  // O reset global `* { padding: 0 }` (assets/scss/index.scss) zera o recuo das listas; mesmo valor do painel de leitura (ApoioDigitacaoPanel).
  ...(isProcedimento && {
    '& .ProseMirror ul, & .ProseMirror ol': { my: 0.5, pl: 2.5 },
  }),
  ...(disabled && {
    color: theme.palette.text.disabled,
    '& .ProseMirror': {
      color: theme.palette.text.disabled,
      WebkitTextFillColor: theme.palette.text.disabled,
      '& *': {
        color: 'inherit',
      },
    },
  }),
});

const procedimentoExtensions = [
  StarterKit.configure({
    heading: { levels: [3, 4] },
    code: false,
    codeBlock: false,
    horizontalRule: false,
  }),
  Underline,
  Link.configure({ openOnClick: false }),
  LinkBubbleMenuHandler,
];

const defaultExtensions = [
  StarterKit,
  Underline,
  TextStyle,
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  Link.configure({ openOnClick: false }),
  LinkBubbleMenuHandler,
];

const RichTextEditorV2 = ({ value, onChange, disabled, label, isRequired, rows, variant }) => {
  const isProcedimento = variant === 'procedimento';
  const editor = useEditor({
    extensions: isProcedimento ? procedimentoExtensions : defaultExtensions,
    content: '',
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    const html = value?.startsWith('<') ? value : value ? `<p>${value}</p>` : '';
    if (editor.getHTML() !== html) {
      editor.commands.setContent(html, false);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  const editorContent = (
    <RichTextEditorProvider editor={editor}>
      <LinkBubbleMenu />
      <RichTextField
        disabled={disabled}
        sx={getRichTextFieldSx(disabled)}
        RichTextContentProps={{
          sx: getRichTextContentSx(disabled, rows, isProcedimento),
        }}
        controls={
          !disabled ? (
            isProcedimento ? (
              <MenuControlsContainer>
                <MenuButtonUndo />
                <MenuButtonRedo />
                <MenuDivider />
                <MenuSelectHeading />
                <MenuDivider />
                <MenuButtonBold />
                <MenuButtonItalic />
                <MenuButtonUnderline />
                <MenuButtonStrikethrough />
                <MenuDivider />
                <MenuButtonBulletedList />
                <MenuButtonOrderedList />
                <MenuDivider />
                <MenuButtonBlockquote />
                <MenuButtonEditLink />
                <MenuDivider />
                <MenuButtonRemoveFormatting />
              </MenuControlsContainer>
            ) : (
              <MenuControlsContainer>
                <MenuButtonUndo />
                <MenuButtonRedo />
                <MenuDivider />
                <MenuSelectHeading />
                <MenuDivider />
                <MenuButtonBold />
                <MenuButtonItalic />
                <MenuButtonUnderline />
                <MenuButtonStrikethrough />
                <MenuButtonCode />
                <MenuDivider />
                <MenuButtonAlignLeft />
                <MenuButtonAlignCenter />
                <MenuButtonAlignRight />
                <MenuButtonAlignJustify />
                <MenuDivider />
                <MenuButtonBulletedList />
                <MenuButtonOrderedList />
                <MenuDivider />
                <MenuButtonBlockquote />
                <MenuButtonCodeBlock />
                <MenuButtonHorizontalRule />
                <MenuDivider />
                <MenuButtonEditLink />
                <MenuDivider />
                <MenuButtonRemoveFormatting />
              </MenuControlsContainer>
            )
          ) : null
        }
      />
    </RichTextEditorProvider>
  );

  if (label) {
    return (
      <InputGroupV2 label={label} isRequired={isRequired}>
        {editorContent}
      </InputGroupV2>
    );
  }

  return editorContent;
};

RichTextEditorV2.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  rows: PropTypes.number,
  variant: PropTypes.oneOf(['default', 'procedimento']),
};

RichTextEditorV2.defaultProps = {
  value: '',
  onChange: () => {},
  disabled: false,
  label: null,
  isRequired: false,
  rows: 4,
  variant: 'default',
};

export default RichTextEditorV2;
